import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export interface LabOrder {
  id: string
  lab_order_number: number
  patient_id: string
  patient_name: string
  veterinarian_id: string | null
  vet_name: string | null
  test_name: string
  priority: 'Routine' | 'Urgent' | 'STAT'
  notes: string
  results: string
  reference_ranges: string
  abnormal_flags: string
  status: 'Ordered' | 'Sample Collected' | 'Sent to Lab' | 'Results Received' | 'Reviewed'
  reviewed_by: string | null
  reviewed_by_name: string | null
  reviewed_at: string | null
  date_ordered: string
  date_completed: string | null
  created_at: string
  updated_at: string
}

export interface LabAttachment {
  id: string
  lab_order_id: string
  filename: string
  url: string
  file_type: string
  size: number
  created_at: string
}

interface LabOrderDetailState {
  order: LabOrder | null
  attachments: LabAttachment[]
  loading: boolean
  error: string | null
}

const initialState: LabOrderDetailState = {
  order: null,
  attachments: [],
  loading: false,
  error: null,
}

export const fetchLabOrder = createAsyncThunk(
  'labOrders/fetchOne',
  async (id: string) => {
    const res = await fetch(`/.netlify/functions/lab-orders/${id}`)
    if (!res.ok) throw new Error('Failed to fetch lab order')
    return res.json() as Promise<LabOrder>
  }
)

export const updateLabOrder = createAsyncThunk(
  'labOrders/update',
  async ({ id, data }: { id: string; data: Partial<LabOrder> }) => {
    const res = await fetch(`/.netlify/functions/lab-orders/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to update lab order')
    return res.json() as Promise<LabOrder>
  }
)

export const fetchLabAttachments = createAsyncThunk(
  'labOrders/fetchAttachments',
  async (labOrderId: string) => {
    const res = await fetch(`/.netlify/functions/lab-attachments?lab_order_id=${labOrderId}`)
    if (!res.ok) throw new Error('Failed to fetch attachments')
    return res.json() as Promise<LabAttachment[]>
  }
)

export const addLabAttachment = createAsyncThunk(
  'labOrders/addAttachment',
  async (data: { lab_order_id: string; filename: string; url: string; file_type: string; size: number }) => {
    const res = await fetch('/.netlify/functions/lab-attachments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to add attachment')
    return res.json() as Promise<LabAttachment>
  }
)

export const deleteLabAttachment = createAsyncThunk(
  'labOrders/deleteAttachment',
  async (attachmentId: string) => {
    const res = await fetch(`/.netlify/functions/lab-attachments/${attachmentId}`, {
      method: 'DELETE',
    })
    if (!res.ok) throw new Error('Failed to delete attachment')
    return attachmentId
  }
)

const labOrdersSlice = createSlice({
  name: 'labOrders',
  initialState,
  reducers: {
    clearLabOrder(state) {
      state.order = null
      state.attachments = []
      state.loading = false
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLabOrder.pending, (state) => {
        if (!state.order) state.loading = true
      })
      .addCase(fetchLabOrder.fulfilled, (state, action) => {
        state.order = action.payload
        state.loading = false
        state.error = null
      })
      .addCase(fetchLabOrder.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to load'
      })
      .addCase(updateLabOrder.fulfilled, (state, action) => {
        state.order = action.payload
      })
      .addCase(fetchLabAttachments.fulfilled, (state, action) => {
        state.attachments = action.payload
      })
      .addCase(addLabAttachment.fulfilled, (state, action) => {
        state.attachments.unshift(action.payload)
      })
      .addCase(deleteLabAttachment.fulfilled, (state, action) => {
        state.attachments = state.attachments.filter((a) => a.id !== action.payload)
      })
  },
})

export const { clearLabOrder } = labOrdersSlice.actions
export default labOrdersSlice.reducer
